import React from "react";
import { CryptoAlertData } from "@crypto-trading/common/browser";
import { Clock, ExternalLink } from "lucide-react";
import { ExchangeLogo } from "@/components/ui/ExchangeLogo";
import { exchangeBrand } from "@/lib/exchange-brand";

/** Columns of the Analyze "Incoming Listings" table, shared with its header. */
export const INCOMING_ROW_GRID =
  "grid grid-cols-[28px_minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,0.8fr)_100px_112px] items-center gap-3";

/** "Website", "Twitter" … for a source URL; the raw text otherwise. */
function sourceLabel(source: string): { label: string; href: string | null } {
  if (!/^https?:\/\//i.test(source)) return { label: source || "–", href: null };
  const host = new URL(source).hostname.replace(/^www\./, "");
  if (host === "x.com" || host.endsWith("twitter.com")) return { label: "Twitter", href: source };
  if (host.endsWith("medium.com")) return { label: "Blog", href: source };
  if (host.includes("roadmap") || source.toLowerCase().includes("roadmap")) return { label: "Roadmap", href: source };
  return { label: "Website", href: source };
}
import { formatExchangeDisplay } from "@/utils/formatExchange";
import { TokenIcon } from "@/components/TokenIcon";

interface IncomingListingTileProps {
  alert: CryptoAlertData;
  onAnalyze?: (symbol: string) => void;
  /** "row": a line of the desktop Analyze table; "card": the phone card. */
  variant?: "tile" | "row" | "card";
  rank?: number;
}

export const IncomingListingTile: React.FC<IncomingListingTileProps> = ({
  alert,
  onAnalyze,
  variant = "tile",
  rank,
}) => {
  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const d = new Date(date);
    const diffMs = now.getTime() - d.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffMinutes < 1) return "just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const handleClick = () => onAnalyze?.(alert.currency);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  if (variant === "card") {
    const source = sourceLabel(alert.source);
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className="flex cursor-pointer flex-col gap-3 rounded-xl border border-white/10 bg-[#0d121a]/80 p-3"
      >
        <div className="flex items-center gap-2.5">
          <span className="w-4 text-sm text-[#d4d4d4]">{rank}</span>
          <TokenIcon symbol={alert.currency} logo={alert.tokenMetadata?.logo} size="sm" />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-semibold text-white">{alert.currency}</span>
            <span className="truncate text-[11px] text-[#a3a3a3]">{alert.tokenMetadata?.name ?? "–"}</span>
          </span>
          <span className="whitespace-nowrap rounded-md border border-[#93370d] bg-[#4e1d09] px-2 py-0.5 text-xs font-medium text-[#fdb022]">
            Coming Soon
          </span>
        </div>
        <div className="grid grid-cols-3 text-xs">
          <span className="flex flex-col">
            <span className="text-[#a3a3a3]">Exchange</span>
            <span className="font-medium text-white">{exchangeBrand(alert.exchange).name}</span>
          </span>
          <span className="flex flex-col border-l border-white/10 pl-2">
            <span className="text-[#a3a3a3]">Announced</span>
            <span className="font-medium text-white">{formatTimeAgo(alert.createdAt)}</span>
          </span>
          <span className="flex flex-col border-l border-white/10 pl-2">
            <span className="text-[#a3a3a3]">Source</span>
            {source.href ? (
              <a
                href={source.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 font-medium text-white"
              >
                <ExternalLink className="size-3.5" />
                {source.label}
              </a>
            ) : (
              <span className="truncate font-medium text-white">{source.label}</span>
            )}
          </span>
        </div>
      </div>
    );
  }

  if (variant === "row") {
    const source = sourceLabel(alert.source);
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`${INCOMING_ROW_GRID} cursor-pointer border-b border-white/[0.06] px-4 py-3.5 text-sm transition-colors last:border-b-0 hover:bg-white/[0.03]`}
      >
        <span className="text-[#d4d4d4]">{rank}</span>
        <span className="flex min-w-0 items-center gap-3">
          <TokenIcon symbol={alert.currency} logo={alert.tokenMetadata?.logo} size="md" />
          <span className="flex min-w-0 flex-col">
            <span className="truncate font-medium text-white">{alert.currency}</span>
            <span className="truncate text-xs text-[#a3a3a3]">{alert.tokenMetadata?.name ?? "–"}</span>
          </span>
        </span>
        <span className="flex min-w-0 items-center gap-2 text-white">
          <ExchangeLogo code={alert.exchange} size={28} />
          <span className="truncate">{exchangeBrand(alert.exchange).name}</span>
        </span>
        <span className="min-w-0 text-white">
          {source.href ? (
            <a
              href={source.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 hover:text-[#2dcc89]"
            >
              <ExternalLink className="size-4 shrink-0" />
              {source.label}
            </a>
          ) : (
            <span className="truncate">{source.label}</span>
          )}
        </span>
        <span className="text-white">{formatTimeAgo(alert.createdAt)}</span>
        <span className="flex justify-end">
          <span className="whitespace-nowrap rounded-md border border-[#93370d] bg-[#4e1d09] px-2 py-0.5 text-xs font-medium text-[#fdb022]">
            Coming Soon
          </span>
        </span>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="flex items-center justify-between gap-4 p-4 bg-secondary border border-border rounded-xl hover:bg-tertiary cursor-pointer transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <TokenIcon
          symbol={alert.currency}
          logo={alert.tokenMetadata?.logo}
          size="md"
        />
        <div className="min-w-0">
          <h3 className="font-semibold text-text-primary truncate">
            {alert.currency}
          </h3>
          <p className="text-xs text-text-secondary flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatExchangeDisplay(alert.exchange, alert.exchangeType)} ·{" "}
            {formatTimeAgo(alert.createdAt)}
          </p>
        </div>
      </div>
      <div className="flex-shrink-0">
        <span className="px-3 py-1 rounded-lg bg-amber-500/20 text-amber-400 text-sm font-medium">
          Coming soon
        </span>
      </div>
    </div>
  );
};
